import React from 'react';
import {
  Box,
  Breadcrumbs,
  Chip,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import type { MetricLevelConfig, MetricLevelInstance } from '../../services/reviewEntityMetricLevelService';
import {
  ALL_METRIC_LEVELS,
  AGGREGATE_METRIC_LEVEL,
  PROJECT_METRIC_LEVEL,
  formatMetricLevelLabel,
  instanceOptionLabel,
  sortMetricLevelTypes,
} from '../../utils/metricLevelUtils';

export interface MetricLevelScopeControlsProps {
  availableLevelTypes: string[];
  selectedLevelType: string;
  selectedInstanceId: string | null;
  metricLevelConfig: MetricLevelConfig | null;
  onLevelTypeChange: (levelType: string) => void;
  onInstanceChange: (instanceId: string | null) => void;
  onBreadcrumbNavigate: (levelType: string, instanceId: string | null) => void;
  disabled?: boolean;
}

function ancestorChain(
  instanceId: string,
  instances: MetricLevelInstance[]
): MetricLevelInstance[] {
  const byId = new Map(instances.map((i) => [i.id, i]));
  const chain: MetricLevelInstance[] = [];
  let cur: MetricLevelInstance | undefined = byId.get(instanceId);
  const seen = new Set<string>();
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    chain.unshift(cur);
    cur = cur.parent_instance_id ? byId.get(cur.parent_instance_id) : undefined;
  }
  return chain;
}

const MetricLevelScopeControls: React.FC<MetricLevelScopeControlsProps> = ({
  availableLevelTypes,
  selectedLevelType,
  selectedInstanceId,
  metricLevelConfig,
  onLevelTypeChange,
  onInstanceChange,
  onBreadcrumbNavigate,
  disabled = false,
}) => {
  const instances = metricLevelConfig?.instances || [];
  const levelUpper = (selectedLevelType || PROJECT_METRIC_LEVEL).toUpperCase();
  const showInstanceSelect =
    levelUpper !== ALL_METRIC_LEVELS &&
    levelUpper !== PROJECT_METRIC_LEVEL &&
    levelUpper !== AGGREGATE_METRIC_LEVEL;

  const instancesAtLevel = instances.filter(
    (i) => (i.metric_level_name || '').toUpperCase() === levelUpper
  );

  const childInstances = (() => {
    if (levelUpper === PROJECT_METRIC_LEVEL) {
      return instances.filter((i) => !i.parent_instance_id);
    }
    if (selectedInstanceId) {
      return instances.filter((i) => i.parent_instance_id === selectedInstanceId);
    }
    return [];
  })();

  const crumbs: { levelType: string; instanceId: string | null; label: string }[] = [
    { levelType: PROJECT_METRIC_LEVEL, instanceId: null, label: 'Project' },
  ];
  if (selectedInstanceId) {
    ancestorChain(selectedInstanceId, instances).forEach((inst) => {
      crumbs.push({
        levelType: (inst.metric_level_name || '').toUpperCase() || 'UNKNOWN',
        instanceId: inst.id,
        label: inst.name,
      });
    });
  } else if (showInstanceSelect) {
    crumbs.push({
      levelType: levelUpper,
      instanceId: null,
      label: `All ${formatMetricLevelLabel(levelUpper)}s`,
    });
  }

  const levelOptions = sortMetricLevelTypes(
    Array.from(new Set([PROJECT_METRIC_LEVEL, ...availableLevelTypes.filter(Boolean)]))
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        bgcolor: 'grey.50',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'grey.200',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <AccountTreeIcon sx={{ color: 'primary.main', fontSize: 20 }} />
        <Typography variant="subtitle2" fontWeight={600}>
          Metric level
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Default is the whole project. Change the level or drill into a module, team, sprint, or
        other configured grain.
      </Typography>

      {crumbs.length > 1 && (
        <Breadcrumbs
          separator={<KeyboardArrowRightIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          {crumbs.map((c, index) => {
            const isLast = index === crumbs.length - 1;
            if (isLast) {
              return (
                <Typography key={`${c.levelType}-${c.instanceId || 'all'}`} variant="body2" color="text.primary">
                  {c.label}
                </Typography>
              );
            }
            return (
              <Link
                key={`${c.levelType}-${c.instanceId || 'all'}`}
                component="button"
                variant="body2"
                underline="hover"
                onClick={() => onBreadcrumbNavigate(c.levelType, c.instanceId)}
                sx={{ cursor: 'pointer' }}
              >
                {c.label}
              </Link>
            );
          })}
        </Breadcrumbs>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: childInstances.length ? 2 : 0 }}>
        <FormControl size="small" sx={{ minWidth: 200 }} disabled={disabled}>
          <InputLabel id="metric-level-scope-label">Level</InputLabel>
          <Select
            labelId="metric-level-scope-label"
            label="Level"
            value={
              levelUpper === ALL_METRIC_LEVELS || levelOptions.includes(levelUpper)
                ? levelUpper
                : PROJECT_METRIC_LEVEL
            }
            onChange={(e) => onLevelTypeChange(e.target.value)}
          >
            <MenuItem value={ALL_METRIC_LEVELS}>All levels</MenuItem>
            {levelOptions.map((level) => (
              <MenuItem key={level} value={level}>
                {formatMetricLevelLabel(level)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {showInstanceSelect && instancesAtLevel.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 280 }} disabled={disabled}>
            <InputLabel id="metric-level-instance-label">Instance</InputLabel>
            <Select
              labelId="metric-level-instance-label"
              label="Instance"
              value={selectedInstanceId || ''}
              onChange={(e) => onInstanceChange(e.target.value ? String(e.target.value) : null)}
            >
              <MenuItem value="">All {formatMetricLevelLabel(levelUpper)}s</MenuItem>
              {instancesAtLevel.map((inst) => (
                <MenuItem key={inst.id} value={inst.id}>
                  {instanceOptionLabel(inst, instances)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {childInstances.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Drill down
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {childInstances.map((inst) => (
              <Chip
                key={inst.id}
                size="small"
                variant="outlined"
                color="primary"
                label={`${formatMetricLevelLabel(inst.metric_level_name)}: ${inst.name}`}
                onClick={() =>
                  onBreadcrumbNavigate(
                    (inst.metric_level_name || '').toUpperCase(),
                    inst.id
                  )
                }
                sx={{ textTransform: 'none' }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default MetricLevelScopeControls;
